import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Box,
} from '@mui/material'

interface ConfirmationDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  severity?: 'warning' | 'error' | 'info' | 'success'
  showDetails?: boolean
  detailsData?: {
    title: string
    fields: Array<{
      label: string
      value: string | number | null | undefined
    }>
  }
  warningMessage?: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  showDetails = false,
  detailsData,
  warningMessage,
  maxWidth = 'sm',
  fullWidth = true,
}) => {
  const getSeverityColors = () => {
    switch (severity) {
      case 'error':
        return {
          backgroundColor: '#f8d7da',
          borderColor: '#f5c6cb',
          textColor: '#721c24',
          iconBg: '#dc3545',
        }
      case 'success':
        return {
          backgroundColor: '#d4edda',
          borderColor: '#c3e6cb',
          textColor: '#155724',
          iconBg: '#28a745',
        }
      case 'info':
        return {
          backgroundColor: '#d1ecf1',
          borderColor: '#bee5eb',
          textColor: '#0c5460',
          iconBg: '#17a2b8',
        }
      default: // warning
        return {
          backgroundColor: '#fff3cd',
          borderColor: '#ffeaa7',
          textColor: '#856404',
          iconBg: '#ffc107',
        }
    }
  }

  const getSeverityIcon = () => {
    switch (severity) {
      case 'error':
        return '❌'
      case 'success':
        return '✅'
      case 'info':
        return 'ℹ️'
      default: // warning
        return '⚠'
    }
  }

  const colors = getSeverityColors()
  const icon = getSeverityIcon()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        style: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle 
        id="confirmation-dialog-title"
        sx={{
          backgroundColor: colors.backgroundColor,
          borderBottom: `1px solid ${colors.borderColor}`,
          color: colors.textColor,
          fontWeight: 600,
          fontSize: '1.25rem',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Box
          component="span"
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: colors.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {icon}
        </Box>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ padding: '24px' }}>
        <DialogContentText 
          id="confirmation-dialog-description"
          sx={{
            color: '#495057',
            fontSize: '1rem',
            lineHeight: 1.6,
            marginBottom: '16px',
            marginTop: '16px',
            fontWeight: 'bold'
          }}
        >
          {message}
        </DialogContentText>
        
        {showDetails && detailsData && (
          <Box
            sx={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              borderLeft: '4px solid #007bff'
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: '#495057',
                fontWeight: 600,
                marginBottom: '12px',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {detailsData.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {detailsData.fields.map((field, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: '#6c757d', fontSize: '0.875rem', fontWeight: 500 }}>
                    {field.label}:
                  </Typography>
                  <Typography sx={{ color: '#495057', fontSize: '0.875rem', fontWeight: 600 }}>
                    {field.value || 'N/A'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        
        {warningMessage && (
          <Box
            sx={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: colors.backgroundColor,
              borderRadius: '8px',
              border: `1px solid ${colors.borderColor}`
            }}
          >
            <Typography
              sx={{
                color: colors.textColor,
                fontSize: '1.1rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {warningMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          padding: '16px 24px 24px',
          gap: '12px',
          justifyContent: 'flex-end'
        }}
      >
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderColor: '#6c757d',
            color: '#6c757d',
            '&:hover': {
              borderColor: '#495057',
              backgroundColor: '#f8f9fa'
            },
            fontWeight: 500,
            padding: '8px 20px'
          }}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained"
          sx={{
            backgroundColor: severity === 'error' ? '#dc3545' : '#007bff',
            '&:hover': {
              backgroundColor: severity === 'error' ? '#c82333' : '#0056b3'
            },
            fontWeight: 500,
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog 